import {Inject, Injectable, Renderer2, RendererFactory2, signal} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import {environment} from '../environments/environment';
import {DOCUMENT} from "@angular/common";
import {map} from "rxjs";

const CONSENT_KEY = 'cookie_consent';
const META_PIXEL_ID = '1080639933430057';

type Consent = 'granted' | 'denied';

@Injectable({
    providedIn: 'root'
})
export class GoogleAnalyticsService {

    private loaded = false
    private welcomeConversionPending = false
    private renderer2: Renderer2
    readonly consentBannerVisible = signal(false)

    constructor(private rendererFactory2: RendererFactory2, @Inject(DOCUMENT) private _document: Document, private router: Router) {
        this.renderer2 = this.rendererFactory2.createRenderer(null, null);
    }

    init() {
        if (this.isDisabled() || this.loaded) return

        const consent = this.storedConsent()
        if (consent === 'granted') {
            whenIdleOrInteracted(this._document.defaultView!, () => this.load())
        } else if (!consent) {
            this.consentBannerVisible.set(true)
        }
    }

    acceptCookies() {
        this.storeConsent('granted')
        if (!this.isDisabled()) this.load()
    }

    rejectCookies() {
        this.storeConsent('denied')
    }

    showCookiePreferences() {
        this.consentBannerVisible.set(true)
    }

    private storedConsent(): Consent | null {
        try {
            return localStorage.getItem(CONSENT_KEY) as Consent | null
        } catch {
            return null
        }
    }

    private storeConsent(consent: Consent) {
        const changedToDenied = consent === 'denied' && this.loaded
        try {
            localStorage.setItem(CONSENT_KEY, consent)
        } catch {
        }
        this.consentBannerVisible.set(false)
        // Scripts already running cannot be unloaded: a reload is the only way to stop them.
        if (changedToDenied) this._document.defaultView?.location.reload()
    }

    private load() {
        if (this.loaded) return

        const analyticsId = environment.googleAnalytics.id;
        this.setupPagePathTagging(analyticsId);

        const script = this.renderer2.createElement('script');
        script.src = `https://www.googletagmanager.com/gtag/js?id=${analyticsId}`;
        script.async = true;
        this.renderer2.appendChild(this._document.body, script);

        this.loadMetaPixel();
    }

    private loadMetaPixel() {
        const window = this._document.defaultView as any
        if (!window || window.fbq) return

        const fbq: any = function () {
            fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments)
        }
        fbq.push = fbq
        fbq.loaded = true
        fbq.version = '2.0'
        fbq.queue = []
        window.fbq = window._fbq = fbq

        const script = this.renderer2.createElement('script');
        script.src = 'https://connect.facebook.net/en_US/fbevents.js';
        script.async = true;
        this.renderer2.appendChild(this._document.head, script);

        fbq('init', META_PIXEL_ID)
        fbq('track', 'PageView')
    }

    private fbq(...args: any[]) {
        (this._document.defaultView as any)?.fbq?.(...args)
    }

    private isDisabled() {
        return !environment.googleAnalytics.enabled;
    }

    private gtag(...args: any[]) {
        const window = this._document.defaultView as any
        if (!window) return

        window.dataLayer = window.dataLayer || []
        window.gtag = window.gtag || function () {
            window.dataLayer.push(arguments)
        }
        window.gtag(...args)
    }

    sendContactRequestClicked() {
        if (this.isDisabled()) return

        this.gtag('event', 'contact_request_click')
    }

    sendJobRequestClicked() {
        if (this.isDisabled()) return

        this.gtag('event', 'jobseeker_request_click')
    }

    sendAdsBaseConversion() {
        if (this.isDisabled()) return

        this.gtag('event', 'conversion', {
            'send_to': 'AW-17041425883/Nz4SCODWk9kaENuL_r0_',
        });

        this.gtag('event', 'base_package_clicked')
        this.fbq('track', 'AddToCart');
    }

    sendAdsStandardConversion() {
        if (this.isDisabled()) return

        this.gtag('event', 'conversion', {
            'send_to': 'AW-17041425883/cYh3COPWk9kaENuL_r0_',
        });

        this.gtag('event', 'standard_package_clicked')
        this.fbq('track', 'AddToCart');
    }

    sendAdsPremiumConversion() {
        if (this.isDisabled()) return

        this.gtag('event', 'conversion', {
            'send_to': 'AW-17041425883/9vVjCObWk9kaENuL_r0_',
        });

        this.gtag('event', 'premium_package_clicked')
        this.fbq('track', 'AddToCart');
    }

    sendAdsSubscriptionConversion() {
        if (this.isDisabled()) return

        this.gtag('event', 'conversion', {
            'send_to': 'AW-17041425883/7RDdCJOrqcoaENuL_r0_',
        });

        this.gtag('event', 'subscription_package_clicked')
        this.fbq('track', 'CompleteRegistration');
    }

    private setupPagePathTagging(analyticsId: string) {
        if (this.isDisabled()) return

        this.loaded = true
        this.gtag('js', new Date())
        this.gtag('config', analyticsId)
        this.gtag('config', 'AW-17041425883')

        if (this.welcomeConversionPending) {
            this.welcomeConversionPending = false
            this.sendAdsWelcomePageConversion()
        }

        this.router.events.pipe(filter(event => event instanceof NavigationEnd), map(value => value as NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                    this.gtag('config', analyticsId, {'page_path': event.urlAfterRedirects});
                }
            )
    }

    sendAdsWelcomePageConversion() {
        if (this.isDisabled()) return

        if (this.loaded) {
            this.gtag('event', 'conversion', {'send_to': 'AW-17041425883/0FJtCKrNut4aENuL_r0_'});
            this.gtag('event', 'home_page_visited')
        } else {
            this.welcomeConversionPending = true
        }
    }
}

const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'scroll'];
const IDLE_DELAY_MS = 5000;

export function whenIdleOrInteracted(window: Window, callback: () => void) {
    let done = false;
    const run = () => {
        if (done) return;
        done = true;
        INTERACTION_EVENTS.forEach(event => window.removeEventListener(event, run));
        callback();
    };
    INTERACTION_EVENTS.forEach(event => window.addEventListener(event, run, {once: true, passive: true}));
    if (window.document.readyState === 'complete') window.setTimeout(run, IDLE_DELAY_MS);
    else window.addEventListener('load', () => window.setTimeout(run, IDLE_DELAY_MS), {once: true});
}
